import React from 'react';
import styled from 'styled-components';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';

export type FeedbackTone = 'success' | 'error' | 'info' | 'warning';

export interface ActionFeedbackState {
  tone: FeedbackTone;
  message: string;
}

const colors: Record<FeedbackTone, { border: string; background: string; text: string }> = {
  success: { border: 'rgba(52, 211, 153, 0.5)', background: 'rgba(6, 78, 59, 0.96)', text: '#d1fae5' },
  error: { border: 'rgba(248, 113, 113, 0.55)', background: 'rgba(127, 29, 29, 0.96)', text: '#fee2e2' },
  info: { border: 'rgba(56, 189, 248, 0.5)', background: 'rgba(12, 74, 110, 0.96)', text: '#e0f2fe' },
  warning: { border: 'rgba(251, 191, 36, 0.5)', background: 'rgba(69, 45, 10, 0.96)', text: '#fef3c7' },
};

const FixedToast = styled.div<{ $tone: FeedbackTone }>`
  position: fixed;
  right: 1.5rem;
  bottom: 1.5rem;
  z-index: 10000;
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  width: min(26rem, calc(100vw - 2rem));
  padding: 0.85rem 0.9rem;
  border: 1px solid ${p => colors[p.$tone].border};
  border-radius: 12px;
  background: ${p => colors[p.$tone].background};
  box-shadow: 0 22px 70px rgba(2, 6, 23, 0.56);
  color: ${p => colors[p.$tone].text};
  font-size: 0.78rem;
  line-height: 1.45;
`;

const CloseButton = styled.button`
  margin-left: auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
`;

const LocalMessage = styled.div<{ $tone: FeedbackTone }>`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid ${p => colors[p.$tone].border};
  border-radius: 9px;
  background: ${p => colors[p.$tone].background};
  color: ${p => colors[p.$tone].text};
  font-size: 0.74rem;
  line-height: 1.45;
`;

const FeedbackIcon: React.FC<{ tone: FeedbackTone }> = ({ tone }) => {
  if (tone === 'success') return <CheckCircle2 size={17} aria-hidden="true" />;
  if (tone === 'error' || tone === 'warning') return <AlertCircle size={17} aria-hidden="true" />;
  return <Info size={17} aria-hidden="true" />;
};

export const ViewportToast: React.FC<{
  feedback: ActionFeedbackState | null;
  onClose: () => void;
}> = ({ feedback, onClose }) => feedback ? (
  <FixedToast $tone={feedback.tone} role={feedback.tone === 'error' || feedback.tone === 'warning' ? 'alert' : 'status'}>
    <FeedbackIcon tone={feedback.tone} />
    <span>{feedback.message}</span>
    <CloseButton aria-label="Dismiss notification" onClick={onClose}><X size={16} /></CloseButton>
  </FixedToast>
) : null;

export const ActionLocalFeedback: React.FC<{ feedback: ActionFeedbackState | null }> = ({ feedback }) =>
  feedback ? (
    <LocalMessage $tone={feedback.tone} role={feedback.tone === 'error' || feedback.tone === 'warning' ? 'alert' : 'status'}>
      <FeedbackIcon tone={feedback.tone} />
      <span>{feedback.message}</span>
    </LocalMessage>
  ) : null;
